import type { OTBreakStarts } from '@/types/api';
import { NUM_SLOTS } from '@/config/modeConfig';
import { noBreakTimeTicked } from '@/utils/otBreakStarts';
import { slotToHhmm } from '@/utils/partialTiming';

/** How far past HOTO the "stays past HOTO to" picker offers, in 15-min slots. */
const MAX_OVERSTAY_SLOTS = 8;

function fmtTime(t: string) { return `${t.slice(0, 2)}:${t.slice(2)}`; }

interface OTHotoStripProps {
  /** Shift end — the strip shows only for a window running to it. */
  hoto: string;
  /** When the officer leaves (the shift's default until the user picks). */
  staysUntil: string;
  startHour: number;
  onChange: (hhmm: string) => void;
}

/**
 * "OT stays past HOTO to [hhmm]" — for an OT window ending at the end of the shift. Hatched
 * because that time is after the roster stops: the next team plans it, but this officer's
 * breaks are counted to it. Defaults to the shift's admin setting; saved with each officer.
 */
export function OTHotoStrip({ hoto, staysUntil, startHour, onChange }: OTHotoStripProps) {
  const choices = Array.from({ length: MAX_OVERSTAY_SLOTS + 1 }, (_, i) => slotToHhmm(NUM_SLOTS + i, startHour));
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10,
        padding: '5px 10px', borderRadius: 6, border: '1px dashed #d97706',
        background: 'repeating-linear-gradient(135deg, #fef3c7 0 6px, #fffbeb 6px 12px)',
      }}
    >
      <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>OT stays past HOTO to</span>
      <select
        aria-label="OT stays past HOTO to"
        value={staysUntil}
        onChange={e => onChange(e.target.value)}
        style={{ width: 'auto', padding: '2px 4px', border: '1px solid #fde68a', borderRadius: 4, fontSize: 12, background: 'white', color: '#78350f' }}
      >
        {choices.map(t => (
          <option key={t} value={t}>{t === hoto ? `${fmtTime(t)} (leaves at HOTO)` : fmtTime(t)}</option>
        ))}
      </select>
    </div>
  );
}

interface OTBreakTimesProps {
  breakStarts: OTBreakStarts;
  checked: Set<string>;
  onToggle: (time: string) => void;
  lastBreakOn: boolean;
  onLastBreakToggle: (on: boolean) => void;
}

/**
 * The OT form's editable checklist of break start times. Every time listed comes from the
 * backend's OT break rule; this only renders and edits it.
 */
export function OTBreakTimes({
  breakStarts, checked, onToggle, lastBreakOn, onLastBreakToggle,
}: OTBreakTimesProps) {
  const { hoto, stays_until: staysUntil, options } = breakStarts;
  const noneTicked = noBreakTimeTicked(breakStarts, checked, lastBreakOn);

  return (
    <div style={{ borderTop: '1px solid #fde68a', paddingTop: 7 }}>
      <div style={{ fontSize: 9, color: '#92400e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        Break may start at
      </div>
      {options.length === 0 ? (
        <p style={{ fontSize: 11, color: '#a16207', margin: 0 }}>This window is too short for a break.</p>
      ) : (
        <div role="group" aria-label="Break may start at" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {options.map(o => {
            const isLast = o.kind === 'last';
            const blocked = o.kind === 'blocked';
            const on = isLast ? lastBreakOn : checked.has(o.time);
            const title = blocked
              ? `Back to only ${o.counter_after_min} min of counter after a break here — shorter than usual; tick to allow it anyway`
              : isLast
                ? 'Last break: work straight through, then break until the officer leaves. Used if it keeps more counters running.'
                : `${o.counter_after_min} min of counter after the break`;
            return (
              <button
                key={`${o.kind}-${o.time}`}
                type="button"
                role="checkbox"
                aria-checked={on}
                title={title}
                onClick={() => (isLast ? onLastBreakToggle(!lastBreakOn) : onToggle(o.time))}
                style={{
                  padding: '2px 7px', borderRadius: 12, fontSize: 11, lineHeight: 1.5,
                  fontVariantNumeric: 'tabular-nums',
                  cursor: 'pointer',
                  border: `1px solid ${on ? '#d97706' : '#fde68a'}`,
                  background: on ? '#fde68a' : 'white',
                  color: '#78350f',
                  fontWeight: on ? 700 : 400,
                }}
              >
                {on ? '✓ ' : ''}{fmtTime(o.time)}{isLast ? ' last' : ''}
              </button>
            );
          })}
        </div>
      )}
      {lastBreakOn && options.some(o => o.kind === 'last') && (
        <p style={{ fontSize: 10, color: '#a16207', marginTop: 4, marginBottom: 0 }}>
          Last break is used if it keeps more counters running
          {hoto && staysUntil !== hoto ? ` — it runs to ${fmtTime(staysUntil)}, past HOTO.` : '.'}
        </p>
      )}
      {noneTicked && (
        <p style={{ fontSize: 10, color: '#dc2626', marginTop: 4, marginBottom: 0 }}>
          Tick at least one break time.
        </p>
      )}
    </div>
  );
}
